import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';

import { AuthSplitLayout } from 'src/layouts/auth-split';

import { Box, Stack, Typography, IconButton } from '@mui/material';
import { LanguagePopover } from 'src/layouts/components/language-popover';
import { allLangs } from 'src/locales';
import { Iconify } from 'src/components/iconify';
import { green, red } from '@mui/material/colors';
import { NotificationsDrawer } from 'src/layouts/components/notifications-drawer';
import { pxToRem } from 'src/theme/styles';
import CustomLink from 'src/components/custom-link/link';
import { paths } from '../paths';

export const getUserRoutes = (
  pathname: string,
  token: any,
  settings: any,
  mode: any,
  setMode: any,
  bannerData: any,
  moduleList: any[]
) => {
  const userComponentMap: {
    [key: string]: {
      component: () => Promise<{ default: React.ComponentType<any> }>;
      children?: {
        [childUrl: string]: () => Promise<{ default: React.ComponentType<any> }>;
      };
    };
  } = {
    profile: {
      component: () => import('src/pages/user/profile/profile'),
      children: {
        'submodule/:sub_module': () => import('src/pages/user/user-submodule-page'),
      },
    },
    applications: {
      component: () => import('src/pages/user/applications/index'),
    },
    'available-properties': {
      component: () => import('src/pages/auth/basic/available-properties'),
    },
    conversations: {
      component: () => import('src/pages/user/conversations/index'),
    },
  };

  const userLinks = moduleList.flatMap((section: any) =>
    section.items
      .filter((item: any) => item.enabled !== false) // enabled by default
      .map((item: any) => ({
        label: item.name,
        url: `/${item.url}`,
      }))
  );

  const routeChildren = moduleList.flatMap((section) =>
    section.items
      .filter((item: any) => item.enabled !== false)
      .flatMap((item: any, i: number) => {
        const config = userComponentMap[item.url];
        if (!config) return [];

        const MainComponent = lazy(config.component);

        const mainRoute = {
          path: item.url,
          element: (
            <Suspense key={`${item.url}-${i}`}>
              <MainComponent />
            </Suspense>
          ),
        };

        const childRoutes =
          item.hasListMenuItems && Array.isArray(item.listMenuItems) && config.children
            ? item.listMenuItems
                .filter((sub: any) => sub.enabled !== false)
                .map((sub: any, j: number) => {
                  const subKey = sub.url.replace(/^\/+/, '');
                  const subImportFunc = config.children?.[subKey];
                  if (!subImportFunc) return null;

                  const SubComponent = lazy(subImportFunc);

                  return {
                    path: `${item.url}${sub.url}`,
                    element: (
                      <Suspense key={`${item.url}${sub.url}-${j}`}>
                        <SubComponent />
                      </Suspense>
                    ),
                  };
                })
                .filter(Boolean)
            : [];

        return [mainRoute, ...childRoutes];
      })
  );

  const headerConfig = moduleList.find((section) => section?.header)?.header || {};
  const centerAreaItems = headerConfig?.centerArea || [];
  const rightAreaItems = headerConfig?.rightArea || [];

  return [
    {
      element: (
        <AuthSplitLayout
          section={{
            bgImage: {
              dark: '/assets/background/mountains_bg.png',
              light: '/assets/background/mountains_bg_light.png',
            },
            imgUrl: false,
            renderBackButton: false,
            showLogout: true,
            customSection: (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 5,
                }}
              >
                {userLinks.map((path, idx) => (
                  <CustomLink
                    key={`${path.label}-${idx}`}
                    to={path.url}
                    label={path.label}
                    style={{ color: pathname.includes(path.url) ? '#ffd400' : 'white' }}
                  />
                ))}
              </Box>
            ),
          }}
          header={{
            slots: {
              rightArea: (
                <Box display="flex" alignItems="center" gap={1.5}>
                  {/* <Box>
                    {centerAreaItems
                      .filter((item: any) => item?.enabled !== false)
                      .map((item: any, idx: number) => {
                        if (item.type === 'user-banner') {
                          return (
                            <Stack
                              key={`center-${idx}`}
                              direction="row"
                              spacing={2}
                              sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                marginLeft: 'auto',
                                marginRight: 2,
                                width: 'max-content',
                                height: '100%',
                                padding: 1.5,
                                border: `2px dashed ${mode === 'light' ? green[900] : green[300]}`,
                                borderRadius: 2,
                                background: `rgba(200, 230, 201,${mode === 'light' ? '0.8' : '0.3'})`,
                              }}
                            >
                              <Box>
                                <Typography variant="subtitle1" sx={{ display: 'flex' }}>
                                  Username:
                                  {token?.payload?.user?.Username ||
                                  token?.payload?.preferred_username ? (
                                    <CustomLink
                                      to="/profile"
                                      label={`${
                                        token?.payload?.user?.Username ||
                                        token.payload.preferred_username
                                      }`}
                                      style={{
                                        marginLeft: 5,
                                        color: mode === 'light' ? '#e42d16' : '#ffd400',
                                      }}
                                    />
                                  ) : (
                                    <Typography component="span" sx={{ ml: 1 }}>
                                      N/A
                                    </Typography>
                                  )}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="subtitle1">
                                  Tracking Number: {bannerData?.TrackingNumber || 'N/A'}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="subtitle1">
                                  {' '}
                                  Status: {bannerData?.StageCode || 'N/A'}
                                </Typography>
                              </Box>
                            </Stack>
                          );
                        }
                        return null;
                      })}
                  </Box> */}
                  {rightAreaItems
                    .filter((item: any) => item?.enabled !== false)
                    .map((item: any, idx: number) => {
                      switch (item.type) {
                        case 'link':
                          return (
                            <CustomLink
                              key={`link-${idx}`}
                              to={item.href}
                              target={item.target}
                              label={item.label}
                              style={{
                                fontWeight: 600,
                                lineHeight: 22 / 14,
                                fontSize: pxToRem(14),
                                color: 'inherit',
                              }}
                            />
                          );
                        case 'notifications':
                          return <NotificationsDrawer key={`notifications-${idx}`} item={item} />;
                        case 'language-switcher':
                          return <LanguagePopover key={`localization-${idx}`} data={allLangs} />;
                        case 'theme-toggle':
                          return (
                            <IconButton
                              key={`theme-${idx}`}
                              onClick={() => {
                                settings.onUpdateField(
                                  'colorScheme',
                                  mode === 'light' ? 'dark' : 'light'
                                );
                                setMode(mode === 'light' ? 'dark' : 'light');
                              }}
                            >
                              <Iconify
                                icon={
                                  mode === 'dark'
                                    ? 'line-md:moon-filled-to-sunny-filled-loop-transition'
                                    : 'line-md:moon-rising-filled-loop'
                                }
                                height={48}
                                color={mode === 'dark' ? red[400] : '-moz-initial'}
                              />
                            </IconButton>
                          );
                        default:
                          return null;
                      }
                    })}
                </Box>
              ),
            },
          }}
        >
          <Outlet />
        </AuthSplitLayout>
      ),
      children: [
        {
          children: routeChildren,
        },
      ],
    },
  ];
};
